import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Flex, theme } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';

import { SearchPerformanceDomainDeadEnd } from '../gsc-connection';
import { INTEGRATIONS_ROUTE } from '../SearchPerformanceRoutes';
import useTrackSearchPerformanceEvent from '../useTrackSearchPerformanceEvent';
import GscManageDomainsPicker from './GscManageDomainsPicker';
import GscManageDomainsSkeleton from './GscManageDomainsSkeleton';
import GscSettingsReconnectNeeded from './GscSettingsReconnectNeeded';
import useGscMintedSession from './useGscMintedSession';

const STRINGS = {
  back: 'Back to Integrations',
  tryAgain: 'Try again',
};

type Props = {
  syncedUrls: string[];
  lostUrls: string[];
  /** `/gsc/status` aggregate; null on an older rest-api. */
  connectedAccountEmail: string | null;
};

function GscManageDomains({ syncedUrls, lostUrls, connectedAccountEmail }: Props) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const trackEvent = useTrackSearchPerformanceEvent();
  const mint = useGscMintedSession();

  const handleSaved = ({ addedCount, removedCount }: { addedCount: number; removedCount: number }) => {
    trackEvent('Search Performance: Manage Domains Saved', {
      added_count: addedCount,
      removed_count: removedCount,
    });
    navigate(INTEGRATIONS_ROUTE);
  };

  const backButton = (
    <Button
      type="link"
      icon={<ArrowLeftOutlined />}
      style={{ paddingLeft: 0 }}
      onClick={() => navigate(INTEGRATIONS_ROUTE)}
    >
      {STRINGS.back}
    </Button>
  );

  if (mint.failure === 'reconnect_required') {
    const lostUrlsToBlame = syncedUrls.length === 0 ? lostUrls : [];
    return <GscSettingsReconnectNeeded lostUrls={lostUrlsToBlame} />;
  }

  if (mint.failure === 'failed') {
    return (
      <Flex vertical gap={token.marginLG}>
        <SearchPerformanceDomainDeadEnd reason="session_load_failed" />
        <Flex justify="space-between" align="center">
          {backButton}
          <Button type="primary" size="large" onClick={mint.retry}>
            {STRINGS.tryAgain}
          </Button>
        </Flex>
      </Flex>
    );
  }

  if (mint.isMinting || !mint.connection) {
    return <GscManageDomainsSkeleton />;
  }

  return (
    <GscManageDomainsPicker
      // Remint reuses the session id; mintCount is what forces a fresh seed.
      key={`${mint.connection.sessionId}:${mint.mintCount}`}
      sessionId={mint.connection.sessionId}
      sourceId={mint.connection.sourceId}
      syncedUrls={syncedUrls}
      lostUrls={lostUrls}
      connectedAccountEmail={connectedAccountEmail}
      onSaved={handleSaved}
      onRetryMint={mint.retry}
    />
  );
}

export default GscManageDomains;
